import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Courtenay = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.courtenay:About Courtenay Paragraph 1">
          Nestled within the scenic embrace of Vancouver Island in{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Courtenay flourishes as a captivating blend of natural beauty and
          cultural vibrancy. Surrounded by ocean and mountains, the city offers
          year-round outdoor pursuits, from kayaking to skiing. Its downtown
          exudes historic charm, with boutique shops and a lively arts and
          cultural scene. Courtenay’s strong community spirit is evident in
          local events, fostering a sense of camaraderie. Amid the Comox
          Valley’s agricultural bounty, farm-to-table experiences thrive.
          Despite Vancouver Island’s allure, Courtenay maintains a manageable
          cost of living, making it a desirable escape within the realm of{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,500 - $2,500',
          bed2: '$1,800 - $2,800',
          bed3: '$2,500 - $3,700'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.courtenay:Apartment Hunting in Courtenay Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Courtenay
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to our
            guides on{' '}
            <LinkText
              href="https://liv.rent/blog/neighbourhoods/
"
            >
              neighbourhoods in Canada
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Apartments for rent Courtenay Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Courtenay
              </LinkText>{' '}
              provide a compact and convenient living space for those seeking a
              place to call home. These apartments are suitable for singles or
              small families looking for a cozy and comfortable space. Many
              apartments for rent in Courtenay are strategically located near
              the city’s amenities, such as shops, restaurants, and public
              transportation, making them a popular choice for those who
              prioritize convenience and accessibility. So, if you’re looking to
              rent an apartment in Courtenay, you’re sure to find the perfect
              option that meets your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Houses for rent Courtenay Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Courtenay
              </LinkText>{' '}
              offer more space, including gardens and outdoor areas, making it
              ideal for larger families or individuals seeking a private and
              homey feel. The houses in Courtenay range from older, character
              homes to newer developments, providing a variety of styles to suit
              different preferences.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Townhouses for rent Courtenay Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Courtenay
              </LinkText>{' '}
              offer a middle ground between houses and apartments. They provide
              the space and privacy of a house while still offering some of the
              conveniences of communal living. These townhouses are often part
              of planned communities and are popular among small families and
              professionals who desire a balance between privacy and community.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Basements for rent Courtenay Paragraph">
              Basements for rent in Courtenay are a common and affordable
              housing option, particularly appealing to students or single
              professionals. These basement units are typically part of larger
              homes and offer a more budget-friendly choice, although they may
              have limitations in terms of light and space compared to
              above-ground options.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Condos for rent Courtenay Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Courtenay
              </LinkText>{' '}
              offer a contemporary and maintenance-free lifestyle. These modern
              condominiums frequently come with extra amenities, such as fitness
              centers or shared spaces. Condos for rent are especially
              attractive to young professionals and downsizers seeking a blend
              of convenience and community.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Rooms for rent Courtenay Paragraph">
              <LinkText href={links.room}>Rooms for rent in Courtenay</LinkText>{' '}
              are the most affordable option, favored by students, seasonal
              workers, or those seeking a flexible living arrangement. It often
              involves sharing common spaces like kitchens and living rooms,
              fostering a sense of community and a shared living experience.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Short term rentals Courtenay Paragraph">
              Courtenay offers a variety of employment opportunities and has
              access to quality educational institutions. This can make it an
              appealing location for those considering{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>,
              although the extent of its popularity for short-term renting can
              vary. Generally, short-term rentals see increased demand in many
              cities, especially during peak seasons such as the summer months
              and at the beginning of academic semesters. The flexibility that
              short-term rentals provide is often a significant consideration
              for renters, influencing their decision on{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                whether or not short-term rentals suit their needs
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.courtenay:Cheap rentals Courtenay Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Courtenay
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Courtenay, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.courtenay:Living in Courtenay Paragraph">
            Living in Courtenay, British Columbia, offers a unique blend of
            small-town charm and stunning natural beauty. Nestled in the heart
            of the Comox Valley, this coastal city is known for its lush
            forests, pristine beaches, and the majestic Comox Glacier.
            Courtenay’s community is warm and welcoming, providing a range of
            amenities for both residents and visitors. From delectable local
            cuisine to a vibrant arts scene and a variety of recreational
            activities, Courtenay has something for everyone. The city boasts
            exceptional quality of life with clean air, safe neighborhoods, and
            a relaxed pace of life that sets it apart. Its picturesque views and
            tranquil ambiance make Courtenay an ideal place for those seeking a
            serene and peaceful living environment.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.courtenay:Living in Courtenay Paragraph 2">
            If you’re not sure that Courtenay is the right city for you, check
            out some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.courtenay:Popular Neighbourhoods Courtenay Paragraph">
          Like any city, Courtenay is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Downtown Courtenay, Crown Isle, East Courtenay, West
          Courtenay, Comox Valley, Cumberland, Royston, and Courtenay East. For
          more info about Courtenay, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.courtenay:Local Landmarks Courtenay Paragraph">
          Courtenay, British Columbia, is renowned for its rich collection of
          local landmarks that showcase the town’s natural beauty and historical
          significance. The Courtenay Airpark, a hub for aviation enthusiasts,
          offers a glimpse into the area’s aviation history. The Courtenay
          Museum, with its fascinating exhibits and artifacts, provides a window
          into the town’s past. The breathtaking scenery of the Courtenay River
          Estuary can be admired from the Courtenay Riverway, a popular trail
          for walkers and cyclists. Another gem is the Puntledge River, where
          visitors can enjoy kayaking and fishing. Lastly, the scenic views from
          the Courtenay Marina and the serene atmosphere of the Courtenay
          Estuary Nature Reserve make them must-visit destinations. These
          landmarks contribute to the unique charm and character of Courtenay.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Courtenay
